import React from "react";
import {
  FaBuilding,
  FaCalendarAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaRoute,
  FaUserFriends,
} from "react-icons/fa";

import {
  DEFAULT_GRADE_IMAGE,
  GRADE_IMAGES,
} from "../data/kataOptions";

import {
  formatDate,
  getDepartmentLabel,
  getRoleLabel,
  sanitizePhoneForLink,
} from "../utils/kataUtils";

import ModalShell from "../ui/ModalShell";

export default function PartenaireDetailsModal({
  partenaire,
  isOpen,
  onClose,
}) {
  if (!partenaire) {
    return null;
  }

  const gradeImage =
    GRADE_IMAGES[partenaire.passage_grade] ||
    DEFAULT_GRADE_IMAGE;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={`${partenaire.prenom} ${partenaire.nom}`}
      subtitle="Annonce de recherche d’un partenaire kata"
      maxWidth="max-w-3xl"
    >
      <div className="p-5 sm:p-7">
        <div className="overflow-hidden rounded-[1.75rem] bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 p-6 text-white">
          <div className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                Passage préparé
              </p>

              <h3 className="mt-2 text-3xl font-semibold">
                {partenaire.passage_grade}
              </h3>

              <div className="mt-4 flex flex-wrap gap-2">
                {partenaire.kata ? (
                  <span className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold">
                    {partenaire.kata}
                  </span>
                ) : null}

                <span className="rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold">
                  {getRoleLabel(
                    partenaire.role_recherche
                  )}
                </span>
              </div>
            </div>

            <img
              src={gradeImage}
              alt={`Ceinture ${partenaire.passage_grade}`}
              className="h-20 w-48 object-contain"
            />
          </div>
        </div>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <DetailItem
            icon={FaBuilding}
            label="Club"
            value={partenaire.club}
          />

          <DetailItem
            icon={FaMapMarkerAlt}
            label="Localisation"
            value={`${getDepartmentLabel(
              partenaire.departement
            )} · ${partenaire.region}`}
          />

          {partenaire.disponibilites ? (
            <DetailItem
              icon={FaCalendarAlt}
              label="Disponibilités"
              value={partenaire.disponibilites}
            />
          ) : null}

          {partenaire.mobilite ? (
            <DetailItem
              icon={FaRoute}
              label="Mobilité"
              value={partenaire.mobilite}
            />
          ) : null}
        </div>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-white/5">
          <h4 className="flex items-center gap-2 font-semibold text-slate-950 dark:text-white">
            <FaUserFriends className="text-orange-500" />
            Présentation et objectifs
          </h4>

          <p className="mt-4 whitespace-pre-line text-sm leading-7 text-slate-600 dark:text-slate-300">
            {partenaire.content}
          </p>
        </section>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {partenaire.tel ? (
            <a
              href={`tel:${sanitizePhoneForLink(
                partenaire.tel
              )}`}
              className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              <FaPhoneAlt />
              Appeler
            </a>
          ) : null}

          {partenaire.mail ? (
            <a
              href={`mailto:${partenaire.mail}`}
              className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              <FaEnvelope />
              Envoyer un email
            </a>
          ) : null}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Annonce publiée le{" "}
          {formatDate(partenaire.created_at)}
        </p>
      </div>
    </ModalShell>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-4 dark:border-white/10">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300">
          <Icon />
        </span>

        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            {label}
          </p>

          <p className="mt-1 text-sm font-bold leading-6 text-slate-800 dark:text-slate-100">
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}